import _extends from '@babel/runtime/helpers/extends';
import React__default, { forwardRef, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { D as DrawerSurface } from './DrawerSurface-DPMcg51w.js';
import { c as composeRefs } from '../LocalModules/storm-ui-utils/dist/esm/composeRefs-CHPW6wJ8-CHPW6wJ8.js';
import { w as withTaktFallbackId } from '../TaktIdContext/TaktIdContext-C6vdL-6Z.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-B0CzqlN8.js';
import { C as CloseButton } from '../Button/CloseButton/CloseButton-DfGdTBMn.js';

function createSetBottomToVisualViewport(divRef) {
  return function setBottomToVisualViewport() {
    if (divRef.current instanceof HTMLElement && window.visualViewport) {
      const {
        height: viewportHeight,
        offsetTop: viewportOffsetTop
      } = window.visualViewport;
      const {
        innerHeight
      } = window;
      const divStyle = divRef.current.style;
      // if the viewport is zoomed in (pinch on mobile or trackpad)
      if (innerHeight > viewportHeight) {
        /**
         * set the bottom offset to the window height minus the
         * combined height of the current viewport and its top offset
        */
        divStyle.setProperty('bottom', `${innerHeight - viewportHeight - viewportOffsetTop}px`);
        return;
      }
      divStyle.removeProperty('bottom');
    }
  };
}

/**
 * Position the close button and ensure RTL is handled.
 */
const PositionedCloseButton = /*#__PURE__*/styled(CloseButton).withConfig({
  componentId: "sc-storm-ui-40021497__sc-1hcm1dl-0"
})(["position:absolute;inset-block-start:0;inset-inline-end:0;inset-inline-start:auto;padding-inline-start:", ";padding-inline-end:", ";padding-block-start:", ";padding-block-end:", ";"], ({
  theme
}) => theme.spacing.small.mobile, ({
  theme
}) => theme.spacing.small.mobile, ({
  theme
}) => theme.spacing.small.mobile, ({
  theme
}) => theme.spacing.small.mobile);
const DrawerCloseButton = withTaktFallbackId(PositionedCloseButton);
const Overlay = /*#__PURE__*/forwardRef(({
  slideState,
  withCloseButton = true,
  closeButtonLabel = '',
  handleCloseButtonClicked,
  closeButtonProps = {},
  children,
  drawerContainerRef,
  ...rest
}, ref) => {
  const containerRef = useRef(null);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    /**
     * Work-around for Android Firefox bug which causes 56px of whitespace
     * at the bottom of the screen when the ContextualDrawer is opened. On resize, if
     * the visualViewport height is greater than the outerHeight of the window, we
     * assume the whitespace is visible and add 56px to the bottom of the bottom Sheet
     * to account for the whitespace blocking any content. Will remove once the
     * browser bug is fixed.
     */
    if (typeof window !== 'undefined' && 'visualViewport' in window && window.visualViewport) {
      const {
        visualViewport
      } = window;
      const setBottomToVisualViewport = createSetBottomToVisualViewport(containerRef);
      visualViewport.addEventListener('resize', setBottomToVisualViewport);
      visualViewport.addEventListener('scroll', setBottomToVisualViewport);
      setBottomToVisualViewport();
      return () => {
        visualViewport.removeEventListener('resize', setBottomToVisualViewport);
        visualViewport.removeEventListener('scroll', setBottomToVisualViewport);
      };
    }
  }, [containerRef]);
  return /*#__PURE__*/React__default.createElement(DrawerSurface, _extends({}, rest, {
    ref: composeRefs(containerRef, ref, drawerContainerRef),
    slideState: slideState,
    withCloseButton: withCloseButton
  }), withCloseButton && /*#__PURE__*/React__default.createElement(DrawerCloseButton, _extends({
    tabIndex: 0,
    size: "base",
    label: closeButtonLabel,
    onClick: handleCloseButtonClicked,
    taktFallbackId: createStormTaktId('drawer-close-button')
  }, closeButtonProps)), children);
});

export { Overlay as O };
//# sourceMappingURL=Overlay-0R_E5ABS.js.map
